import React, { useState, useEffect } from "react";
import { connect } from "react-redux";
import PropTypes from "prop-types";
import { Link } from "react-router-dom";
import { withRouter } from '../../../common/withRouter';
import { ButtonOBSOLETED, DataDefinitionList, Loader, Notification, Message } from "../../../web.common";
import { LayoutContext } from "../../layout/siteLayout";
import PageTop from "../../layout/PageTop";
import withApi from "../../higherOrderComponents/withApi";
import { DisplayDate } from "../../items";
import ImageCropper from "../../misc/ImageCropper.js";
import { Image } from "../Facility";
import { apiErrorText, clubhouseFormError, linkOrText, buttonRowStyle, panelStyle, warningTextStyle, PlaceField, isFacilityKind, isAreaKind, placeMissing, placePayload, placeReady, takesExactPlace } from "./shared";

// The clubhouse with its sports, events, members and wannas. Name, texts and place can be edited here;
// actions on a wanna are on the wanna page.
const ClubhouseInternal = ({ api, match: { params: { slug } } }) => {
  const [data, setData] = useState(null);
  const [memberships, setMemberships] = useState(null);
  const [wannas, setWannas] = useState(null);
  const [notification, setNotification] = useState(null);
  const [isAddingMember, setIsAddingMember] = useState(false);
  const [newMember, setNewMember] = useState({ userId: "", role: "member" });
  const [isBusy, setIsBusy] = useState(false);
  const [invite, setInvite] = useState(null);
  const [edit, setEdit] = useState(null);
  const [removing, setRemoving] = useState(null);
  const [cropping, setCropping] = useState(null);

  useEffect(() => {
    const load = async () => {
      try {
        const [clubhouseData, memberList, wannaList] = await Promise.all([
          api.clubhouseAdmin.getClubhouse(slug),
          api.clubhouseAdmin.getMemberships({ clubhouseSlug: slug, take: 500 }),
          api.clubhouseAdmin.getWannas({ clubhouseSlug: slug, includeDeleted: true, take: 200 }),
        ]);
        setData(clubhouseData);
        setMemberships(memberList);
        setWannas(wannaList);
      } catch (e) {
        setNotification(new Message("error", apiErrorText(e, "Unable to load the clubhouse.")));
      }
    };
    load();
  }, [slug]);

  const createInvite = async () => {
    setIsBusy(true);
    try {
      const created = await api.clubhouseAdmin.createClubhouseInvite(slug, 14);
      setInvite(created);
      setNotification(new Message("success", "Invite link created. Copy it now: it is shown only once."));
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to create the invite.")));
    } finally {
      setIsBusy(false);
    }
  };

  const startEdit = () => {
    const current = data.clubhouse;
    setEdit({
      name: current.name || "",
      subtitle: current.subtitle || "",
      description: current.description || "",
      areaSlug: current.areaSlug || "",
      facilitySlug: current.facilitySlug || "",
      addressText: current.addressText || "",
      latitude: current.latitude != null ? String(current.latitude) : "",
      longitude: current.longitude != null ? String(current.longitude) : "",
      coverPhotoUrl: current.coverPhotoUrl || "",
    });
  };

  // The cover photo is cropped to the facility "huge" size and uploaded like the facility photos; the clubhouse keeps
  // the name of the uploaded picture until Save.
  const pickCover = (e) => {
    const file = e.target.files && e.target.files[0];
    if (!file) return;
    const reader = new FileReader();
    reader.onload = () => setCropping({ src: reader.result, format: file.type });
    reader.readAsDataURL(file);
    e.target.value = "";
  };

  const saveCover = async (croppedImage) => {
    const response = await api.products.saveImage(croppedImage, "-huge", cropping.format);
    setCropping(null);
    if (!response.success) {
      setNotification(new Message("error", response.message || "Unable to upload the picture."));
      return;
    }
    setEdit((current) => ({ ...current, coverPhotoUrl: response.data }));
  };

  const saveEdit = async () => {
    const invalid = clubhouseFormError(edit);
    if (invalid) {
      setNotification(new Message("error", invalid));
      return;
    }
    setIsBusy(true);
    try {
      await api.clubhouseAdmin.updateClubhouse(slug, {
        name: edit.name,
        subtitle: edit.subtitle || null,
        description: edit.description || null,
        coverPhotoUrl: edit.coverPhotoUrl || null,
        ...placePayload(data.clubhouse.kind, edit),
      });
      setData(await api.clubhouseAdmin.getClubhouse(slug));
      setEdit(null);
      setNotification(new Message("success", "Clubhouse saved."));
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to save the clubhouse.")));
    } finally {
      setIsBusy(false);
    }
  };

  const reloadMembers = async () => {
    const [clubhouseData, memberList] = await Promise.all([
      api.clubhouseAdmin.getClubhouse(slug),
      api.clubhouseAdmin.getMemberships({ clubhouseSlug: slug, take: 500 }),
    ]);
    setData(clubhouseData);
    setMemberships(memberList);
  };

  // Koncept §3: the admin is told about new members and can remove people.
  const removeMember = async () => {
    setIsBusy(true);
    try {
      await api.clubhouseAdmin.removeClubhouseMember(slug, removing.userId);
      setNotification(new Message("success", `${removing.userName || "The member"} removed.`));
      setRemoving(null);
      await reloadMembers();
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to remove the member.")));
    } finally {
      setIsBusy(false);
    }
  };

  const addMember = async () => {
    setIsBusy(true);
    try {
      await api.clubhouseAdmin.addClubhouseMember(slug, newMember.userId.trim(), newMember.role);
      setNotification(new Message("success", "Member added."));
      setIsAddingMember(false);
      setNewMember({ userId: "", role: "member" });
      const [clubhouseData, memberList, wannaList] = await Promise.all([
        api.clubhouseAdmin.getClubhouse(slug),
        api.clubhouseAdmin.getMemberships({ clubhouseSlug: slug, take: 500 }),
        api.clubhouseAdmin.getWannas({ clubhouseSlug: slug, includeDeleted: true, take: 200 }),
      ]);
      setData(clubhouseData);
      setMemberships(memberList);
      setWannas(wannaList);
    } catch (e) {
      setNotification(new Message("error", apiErrorText(e, "Unable to add the member.")));
    } finally {
      setIsBusy(false);
    }
  };

  const clubhouse = data?.clubhouse;
  const admins = memberships?.memberships.filter(({ role }) => role === "admin") || [];

  const listData = clubhouse && {
    Slug: clubhouse.slug,
    Name: clubhouse.name,
    Kind: clubhouse.kind,
    Open: clubhouse.isOpen ? "yes" : "no",
    // The detail response has no live count or wanna count: they come from the memberships and wannas lists.
    Members: memberships ? memberships.total : "…",
    Wannas: wannas ? wannas.total : "-",
    ...(isFacilityKind(clubhouse.kind)
      ? { Facility: clubhouse.facilitySlug || <span style={warningTextStyle}>missing: courts nearby cannot be shown</span> }
      : {
        ...(takesExactPlace(clubhouse.kind) ? {
          "Exact place": clubhouse.latitude != null && clubhouse.longitude != null
            ? <a href={`https://www.google.com/maps?q=${clubhouse.latitude},${clubhouse.longitude}`} target="_blank" rel="noopener noreferrer">{clubhouse.addressText || `${clubhouse.latitude}, ${clubhouse.longitude}`}</a>
            : placeMissing(clubhouse) ? <span style={warningTextStyle}>missing: a house needs its address (courts nearby use the area until then)</span> : "-",
        } : {}),
        Area: clubhouse.areaSlug || (isAreaKind(clubhouse.kind) ? <span style={warningTextStyle}>missing: courts nearby cannot be shown</span> : "-"),
      }),
    Cover: clubhouse.coverUrl
      ? <span>{clubhouse.coverPhotoUrl ? "own photo" : "facility photo"}<br /><Image link={clubhouse.coverUrl} /></span>
      : "-",
    "Created At": <DisplayDate>{clubhouse.createdAtUtc}</DisplayDate>,
    "Deleted At": clubhouse.deletedAtUtc ? <DisplayDate>{clubhouse.deletedAtUtc}</DisplayDate> : "-",
    Admins: admins.length ? admins.map(({ userName, userEmail }) => `${userName} (${userEmail})`).join(", ") : "-",
  };

  return (
    <LayoutContext.Consumer>
      {() => (
        <React.Fragment>
          <Notification newMessage={notification} />
          <div className="content-with-right-pane-delimiter">
            <PageTop documentTitle="Clubhouse" documentDescription="Clubhouse" />
            <h2 className="main-page-header main-page-header-with-subheader">Clubhouse</h2>
            <h5 className="main-page-subheader">{slug}</h5>
            <br />

            {cropping ? (
              <ImageCropper imageSrc={cropping.src} aspectRatio={1015 / 425} pictureSize={{ width: 1015, height: 425 }} format={cropping.format} onCancel={() => setCropping(null)} onImageCropped={saveCover} />
            ) : !data ? <Loader hasLoaded={false}>Loading...</Loader> : (
              <React.Fragment>
                <DataDefinitionList listData={listData} classNames={["detail-page-list"]} />

                {!edit ? (
                  <div style={buttonRowStyle}>
                    <ButtonOBSOLETED buttonContent="Edit" disabled={isBusy || !!clubhouse.deletedAtUtc} onClick={startEdit} />
                  </div>
                ) : (
                  <div style={panelStyle}>
                    <h3>Edit clubhouse</h3>
                    <div style={{ display: "grid", gap: "0.75rem", maxWidth: "32rem" }}>
                      <label>Name
                        <input type="text" value={edit.name} onChange={(e) => setEdit({ ...edit, name: e.target.value })} maxLength={80} style={{ display: "block", width: "100%" }} />
                      </label>
                      <PlaceField api={api} kind={clubhouse.kind} place={edit} onChange={(place) => setEdit({ ...edit, ...place })} />
                      <label>Subtitle (optional)
                        <input type="text" value={edit.subtitle} onChange={(e) => setEdit({ ...edit, subtitle: e.target.value })} maxLength={200} style={{ display: "block", width: "100%" }} />
                      </label>
                      <label>Description (optional)
                        <textarea value={edit.description} onChange={(e) => setEdit({ ...edit, description: e.target.value })} maxLength={2000} rows={3} style={{ display: "block", width: "100%" }} />
                      </label>
                      <div>
                        Cover photo (optional{isFacilityKind(clubhouse.kind) ? ": without one the facility's photo is shown" : ""})
                        {edit.coverPhotoUrl && <div><Image link={edit.coverPhotoUrl} /></div>}
                        <input type="file" accept="image/*" onChange={pickCover} style={{ display: "block", marginTop: "0.5rem" }} />
                        {edit.coverPhotoUrl && (
                          <ButtonOBSOLETED buttonContent="Remove photo" disabled={isBusy} onClick={() => setEdit({ ...edit, coverPhotoUrl: "" })} />
                        )}
                      </div>
                    </div>
                    <p style={{ fontSize: "0.9em" }}>Slug and kind cannot be changed.</p>
                    <div style={buttonRowStyle}>
                      <ButtonOBSOLETED buttonContent="Save" disabled={isBusy || !edit.name.trim() || !placeReady(clubhouse.kind, edit)} onClick={saveEdit} />
                      <ButtonOBSOLETED buttonContent="Cancel" disabled={isBusy} onClick={() => setEdit(null)} />
                    </div>
                  </div>
                )}

                <h3>Sports</h3>
                {data.sports.length === 0 ? <p>None</p> : (
                  <ul>
                    {data.sports.map((sport) => (
                      <li key={sport.slug || sport.name}>{sport.name || sport.slug}{sport.playerCount !== undefined && ` (${sport.playerCount} players)`}</li>
                    ))}
                  </ul>
                )}

                <h3>Events</h3>
                {data.events.length === 0 ? <p>None</p> : (
                  <ul>
                    {data.events.map((event, index) => (
                      <li key={event.code || event.id || index}>{event.title || event.name || event.code || event.id}</li>
                    ))}
                  </ul>
                )}

                <h3>Invite</h3>
                <div style={buttonRowStyle}>
                  <ButtonOBSOLETED buttonContent="Create invite link (14 days)" disabled={isBusy} onClick={createInvite} />
                </div>
                {invite && (
                  <div style={panelStyle}>
                    <p>Expires {invite.expiresAtUtc}</p>
                    <p><code style={{ wordBreak: "break-all" }}>{invite.url}</code></p>
                    <p style={{ fontSize: "0.9em" }}>The link is shown only once. Prefix it with the frontend host (for example https://www.wannasport.com).</p>
                    <div style={buttonRowStyle}>
                      <ButtonOBSOLETED buttonContent="Copy link" onClick={() => navigator.clipboard && navigator.clipboard.writeText(invite.url)} />
                      <ButtonOBSOLETED buttonContent="Close" onClick={() => setInvite(null)} />
                    </div>
                  </div>
                )}

                <h3>Members ({memberships ? memberships.total : "-"})</h3>
                <div style={buttonRowStyle}>
                  <ButtonOBSOLETED buttonContent="Add member" disabled={isBusy} onClick={() => setIsAddingMember(true)} />
                </div>
                {isAddingMember && (
                  <div style={panelStyle}>
                    <div style={{ display: "grid", gap: "0.75rem", maxWidth: "32rem" }}>
                      <label>User ID (the account GUID)
                        <input type="text" value={newMember.userId} onChange={(e) => setNewMember({ ...newMember, userId: e.target.value })} style={{ display: "block", width: "100%" }} />
                      </label>
                      <label>Role
                        <select value={newMember.role} onChange={(e) => setNewMember({ ...newMember, role: e.target.value })} style={{ display: "block", width: "100%" }}>
                          <option value="member">member</option>
                          <option value="admin">admin</option>
                        </select>
                      </label>
                    </div>
                    <div style={buttonRowStyle}>
                      <ButtonOBSOLETED buttonContent="Add" disabled={isBusy || !newMember.userId.trim()} onClick={addMember} />
                      <ButtonOBSOLETED buttonContent="Cancel" disabled={isBusy} onClick={() => { setIsAddingMember(false); setNewMember({ userId: "", role: "member" }); }} />
                    </div>
                  </div>
                )}
                {removing && (
                  <div style={panelStyle}>
                    <h3>Remove {removing.userName || removing.userId} from the clubhouse?</h3>
                    <p>They stop being a member and no longer see the clubhouse. Their wannas stay in the clubhouse.</p>
                    <div style={buttonRowStyle}>
                      <ButtonOBSOLETED buttonContent="Confirm remove" disabled={isBusy} onClick={removeMember} />
                      <ButtonOBSOLETED buttonContent="Back" disabled={isBusy} onClick={() => setRemoving(null)} />
                    </div>
                  </div>
                )}
                {memberships && (
                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table">
                      <thead>
                        <tr>
                          <td>Name</td>
                          <td>Email</td>
                          <td>Role</td>
                          <td>Joined</td>
                          <td></td>
                        </tr>
                      </thead>
                      <tbody>
                        {memberships.memberships.map(({ userId, userName, userEmail, role, joinedAtUtc }) => (
                          <tr key={userId}>
                            <td>{linkOrText(`/user/${userId}`, userName)}</td>
                            <td>{userEmail}</td>
                            <td>{role}</td>
                            <td><DisplayDate>{joinedAtUtc}</DisplayDate></td>
                            <td><ButtonOBSOLETED buttonContent="Remove" disabled={isBusy} onClick={() => setRemoving({ userId, userName })} /></td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </article>
                )}

                <h3>Wannas ({wannas ? wannas.total : "-"})</h3>
                {wannas && (
                  <article className="data-table-wrap">
                    <table className="data-lists component-data-table">
                      <thead>
                        <tr>
                          <td>Code</td>
                          <td>Form</td>
                          <td>Status</td>
                          <td>Text</td>
                          <td>Starts</td>
                          <td>Deleted</td>
                        </tr>
                      </thead>
                      <tbody>
                        {wannas.wannas.map((wanna) => (
                          <tr key={wanna.code}>
                            <td><Link to={`/clubhouse-wanna/${encodeURIComponent(wanna.code)}`}>{wanna.code}</Link></td>
                            <td>{wanna.form}</td>
                            <td>{wanna.status}</td>
                            <td>{wanna.text}</td>
                            <td><DisplayDate>{wanna.startsAtUtc}</DisplayDate></td>
                            <td>{wanna.deletedAtUtc ? "yes" : "no"}</td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </article>
                )}
              </React.Fragment>
            )}
          </div>
        </React.Fragment>
      )}
    </LayoutContext.Consumer>
  );
};

const Clubhouse = withRouter(
  withApi(
    connect(
      ({ user }) => ({ isLoggedIn: !!user }),
      {}
    )(ClubhouseInternal)
  )
);

Clubhouse.requiresLogin = true;
Clubhouse.propTypes = { isLoggedIn: PropTypes.bool };

export default Clubhouse;
